import { GeminiClient } from '@/services/gemini/client';

/**
 * Fields we try to pull out of a Brazilian ID document via OCR.
 * Everything optional — the OCR may not see every field on every document.
 */
export interface ExtractedPatientData {
  name?: string;
  birthDate?: string; // ISO YYYY-MM-DD
  cpf?: string;       // digits only
  rg?: string;        // as printed
  gender?: 'M' | 'F';
  motherName?: string;
  fatherName?: string;
  documentType?: 'RG' | 'CNH' | 'CPF' | 'OUTRO';
  /** Raw response for debugging / fallback display. */
  _rawText?: string;
}

const OCR_PROMPT = `Você é um assistente especializado em extrair dados de documentos de identificação brasileiros (RG, CNH, CPF, carteira de identidade, etc.) para cadastro de pacientes em uma clínica médica.

Analise a(s) imagem(ns) do documento fornecida(s) e retorne APENAS um objeto JSON válido com este formato exato:

{
  "name": "NOME COMPLETO COMO IMPRESSO NO DOCUMENTO",
  "birthDate": "YYYY-MM-DD",
  "cpf": "12345678900",
  "rg": "12.345.678-9",
  "gender": "M",
  "motherName": "Nome da mãe",
  "fatherName": "Nome do pai",
  "documentType": "RG"
}

REGRAS IMPORTANTES:
- Retorne SOMENTE o JSON, sem texto antes ou depois, sem blocos de código markdown.
- Se não conseguir ler algum campo com confiança, use null para aquele campo.
- "cpf" deve conter apenas os 11 dígitos, sem pontos ou traços.
- "birthDate" deve estar no formato ISO (YYYY-MM-DD). Converta datas em dd/mm/yyyy.
- "gender" deve ser "M" (masculino) ou "F" (feminino), ou null se ambíguo.
- "documentType" deve ser um dos valores: "RG", "CNH", "CPF", "OUTRO".
- "name" em MAIÚSCULAS como geralmente aparece no documento.
- Se o documento não for de identificação (ex: receita médica, comprovante), retorne {"documentType": "OUTRO"} com os demais campos null.`;

/**
 * Extract patient fields from a photo/scan of an ID document using Gemini OCR.
 * Consumes 3 tokens (ocr action) per call.
 */
export async function extractFromDocument(file: File): Promise<ExtractedPatientData> {
  const result = await GeminiClient.analyzeMultimodal({
    audio: null,
    images: [file],
    pdfs: [],
    text: OCR_PROMPT,
    action: 'ocr',
    template: 'document_ocr',
    interlocutors: 1,
  });

  const raw = (result.markdown || '').trim();
  const parsed = parseJsonLoose(raw);
  if (!parsed) {
    return { _rawText: raw };
  }

  return {
    name: cleanString(parsed.name),
    birthDate: normalizeDate(parsed.birthDate),
    cpf: cleanCpf(parsed.cpf),
    rg: cleanString(parsed.rg),
    gender: normalizeGender(parsed.gender),
    motherName: cleanString(parsed.motherName),
    fatherName: cleanString(parsed.fatherName),
    documentType: normalizeDocType(parsed.documentType),
    _rawText: raw,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Gemini sometimes wraps JSON in ```json fences despite the "no markdown" rule.
 * This is defensive: strip fences, find the first {...} block, and parse it.
 */
function parseJsonLoose(text: string): Record<string, unknown> | null {
  if (!text) return null;
  // Remove fenced blocks
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  const body = fenceMatch ? fenceMatch[1] : text;

  // Find the outer JSON object
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  const slice = body.slice(start, end + 1);

  try {
    return JSON.parse(slice) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function cleanString(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null') return undefined;
  return trimmed;
}

function cleanCpf(v: unknown): string | undefined {
  const s = cleanString(v);
  if (!s) return undefined;
  const digits = s.replace(/\D/g, '');
  return digits.length === 11 ? digits : undefined;
}

function normalizeDate(v: unknown): string | undefined {
  const s = cleanString(v);
  if (!s) return undefined;
  // Already ISO
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return s;
  // dd/mm/yyyy or dd-mm-yyyy
  const br = s.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return undefined;
}

function normalizeGender(v: unknown): 'M' | 'F' | undefined {
  const s = cleanString(v);
  if (!s) return undefined;
  const up = s.toUpperCase();
  if (up.startsWith('M')) return 'M';
  if (up.startsWith('F')) return 'F';
  return undefined;
}

function normalizeDocType(v: unknown): ExtractedPatientData['documentType'] {
  const s = cleanString(v)?.toUpperCase();
  if (s === 'RG' || s === 'CNH' || s === 'CPF' || s === 'OUTRO') {
    return s;
  }
  return 'OUTRO';
}
